import React from 'react';
import { BookOpen, ShoppingCart, ExternalLink, User, Building } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { BookSynopsisCard } from '../../../../shared/components/BookSynopsisCard.js';
import { BookTableOfContentsCard } from '../../../../shared/components/BookTableOfContentsCard.js';

interface BookMetadataGridProps {
  book: Book;
}

export const BookMetadataGrid: React.FC<BookMetadataGridProps> = ({ book }) => {
  return (
    <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-2xs space-y-3">
      <div className="flex items-center gap-1.5 font-bold text-slate-800 text-xs uppercase tracking-wide">
        <BookOpen className="w-4 h-4 text-indigo-600" />
        <span>Metadata Bibliografi Naskah</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs">
        {book.subtitle && (
          <div className="col-span-2 sm:col-span-3 p-2 rounded-lg bg-indigo-50/70 border border-indigo-100 space-y-0.5">
            <span className="text-[10px] text-indigo-700 uppercase font-semibold block">Subjudul Buku</span>
            <span className="font-semibold text-indigo-900 block">{book.subtitle}</span>
          </div>
        )}

        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 space-y-0.5">
          <span className="text-[10px] text-slate-400 uppercase font-semibold block">Kategori / Topik</span>
          <span className="font-medium text-slate-800 truncate block">{book.category}</span>
        </div>

        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 space-y-0.5">
          <span className="text-[10px] text-slate-400 uppercase font-semibold block">Tahun Terbit</span>
          <span className="font-medium text-slate-800 block">{book.publicationYear}</span>
        </div>

        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 space-y-0.5">
          <span className="text-[10px] text-slate-400 uppercase font-semibold block">Edisi / Cetakan</span>
          <span className="font-medium text-slate-800 block">{book.edition || 'Edisi 1 (Perdana)'}</span>
        </div>

        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 space-y-0.5">
          <span className="text-[10px] text-slate-400 uppercase font-semibold block">Jumlah Halaman</span>
          <span className="font-medium text-slate-800 block">{book.pages ? `${book.pages} hlm` : 'Belum Ditentukan'}</span>
        </div>

        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 space-y-0.5">
          <span className="text-[10px] text-slate-400 uppercase font-semibold block">Bahasa Naskah</span>
          <span className="font-medium text-slate-800 block">{book.language || 'Bahasa Indonesia'}</span>
        </div>

        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 space-y-0.5">
          <span className="text-[10px] text-slate-400 uppercase font-semibold block">ISBN Asal (Opsional)</span>
          <span className="font-mono text-slate-800 truncate block">{book.isbnOriginal || 'Tidak Ada'}</span>
        </div>

        {book.purchaseUrl && (
          <div className="col-span-2 sm:col-span-3 p-2 rounded-lg bg-emerald-50/70 border border-emerald-200 flex items-center justify-between gap-2">
            <div>
              <span className="text-[10px] text-emerald-800 uppercase font-semibold block">Tautan Pembelian Buku Resmi</span>
              <span className="font-mono text-[11px] text-emerald-700 truncate block max-w-sm">{book.purchaseUrl}</span>
            </div>
            <a
              href={book.purchaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-1 shrink-0 transition"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              <span>Buka Link</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>

      {/* Author and Publisher Detailed Box */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 flex items-start gap-2">
          <User className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <span className="text-[10px] text-slate-400 font-semibold uppercase block">Penulis Terdaftar</span>
            <p className="font-bold text-slate-800 break-words leading-snug">{book.authorName}</p>
            {book.authorId && (
              <p className="text-[10px] text-slate-400 font-mono truncate">ID: {book.authorId}</p>
            )}
          </div>
        </div>

        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 flex items-start gap-2">
          <Building className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <span className="text-[10px] text-slate-400 font-semibold uppercase block">Penerbit Resmi</span>
            <p className="font-bold text-slate-800 break-words leading-snug">{book.publisherName}</p>
            {book.publisherId && (
              <p className="text-[10px] text-slate-400 font-mono truncate">ID: {book.publisherId}</p>
            )}
          </div>
        </div>
      </div>

      {/* Professional Synopsis & Narrative Overview */}
      <BookSynopsisCard
        synopsis={book.synopsis}
        bookTitle={book.title}
        defaultExpanded={false}
        maxCollapsedLength={200}
      />

      {/* Book Table of Contents (Daftar Isi) */}
      {book.tableOfContents && (
        <BookTableOfContentsCard
          tableOfContents={book.tableOfContents}
          bookTitle={book.title}
          defaultExpanded={false}
          maxCollapsedLength={200}
        />
      )}
    </div>
  );
};
